import { finaleEpisodes, type FinaleStep } from "../domain/finale";
import { shuffled } from "../domain/presentationOrder";
import type { ChallengeDefinition } from "../domain/schema";

interface Options {
  panel: HTMLElement;
  onComplete(challenge: ChallengeDefinition, hintsUsed: number): void;
  onClose(): void;
  speak(text: string): void;
}

export class FinaleChallengeController {
  private challenge?: ChallengeDefinition;
  private hintsUsed = 0;
  private stepIndex = 0;

  constructor(private readonly options: Options) {}

  open(challenge: ChallengeDefinition): void {
    const episode = finaleEpisodes.find(
      ({ challengeId }) => challengeId === challenge.id,
    );
    if (!episode) throw new Error(`Finale episode missing: ${challenge.id}`);
    this.challenge = challenge;
    this.hintsUsed = 0;
    this.stepIndex = 0;
    this.options.panel.hidden = false;
    this.options.panel.innerHTML = `
      <div class="challenge-heading">
        <div><span class="pill">Grand Wildlife Rescue</span><h2 id="challenge-title">${challenge.title}</h2></div>
        <button class="icon-button" data-action="close" type="button" aria-label="Return to the sanctuary">×</button>
      </div>
      <p class="finale-story">${episode.story}</p>
      <div id="challenge-body" class="challenge-body"></div>
      <div class="hint-row">
        <button class="secondary-button secondary-button--compact" data-action="hint" type="button">Need a hint?</button>
        <p id="hint-text" class="hint-text" aria-live="polite">Use evidence from your restored regions. There is no timer and you can retry.</p>
      </div>
      <output id="challenge-feedback" class="challenge-feedback" aria-live="polite"></output>`;
    this.options.panel
      .querySelector("[data-action='close']")
      ?.addEventListener("click", () => this.close());
    this.options.panel
      .querySelector("[data-action='hint']")
      ?.addEventListener("click", () => this.showHint());
    this.renderStep();
    this.options.panel
      .querySelector<HTMLButtonElement>("[data-action='close']")
      ?.focus();
  }

  close(): void {
    this.options.panel.hidden = true;
    this.options.panel.innerHTML = "";
    this.options.onClose();
  }

  private episode() {
    const challenge = this.challenge;
    const episode = finaleEpisodes.find(
      ({ challengeId }) => challengeId === challenge?.id,
    );
    if (!episode) throw new Error("Active finale episode missing");
    return episode;
  }

  private renderStep(): void {
    const episode = this.episode();
    const step = episode.steps[this.stepIndex];
    if (!step) {
      this.finish();
      return;
    }
    const body =
      this.options.panel.querySelector<HTMLElement>("#challenge-body");
    if (!body) return;
    body.innerHTML = `
      <div class="health-step">Rescue clue ${this.stepIndex + 1} of ${episode.steps.length}</div>
      ${renderVisual(step)}
      <p class="challenge-question">${step.prompt}</p>
      <div class="choice-grid choice-grid--stacked">${shuffled(step.options)
        .map(
          (option) =>
            `<button type="button" data-finale-option="${option.id}">${option.label}</button>`,
        )
        .join("")}</div>`;
    for (const button of body.querySelectorAll<HTMLButtonElement>(
      "[data-finale-option]",
    )) {
      button.addEventListener("click", () => {
        if (button.dataset.finaleOption !== step.correctOptionId) {
          this.feedback(
            `That clue does not fit the evidence yet. ${step.explanation}`,
          );
          button.classList.add("try-again");
          return;
        }
        this.feedback(step.explanation, true);
        this.stepIndex += 1;
        window.setTimeout(() => this.renderStep(), 260);
      });
    }
  }

  private feedback(message: string, success = false): void {
    const output = this.options.panel.querySelector<HTMLOutputElement>(
      "#challenge-feedback",
    );
    if (!output) return;
    output.textContent = message;
    output.classList.toggle("challenge-feedback--success", success);
    if (success || this.hintsUsed > 0) this.options.speak(message);
  }

  private showHint(): void {
    const challenge = this.challenge;
    if (!challenge) return;
    this.hintsUsed = Math.min(2, this.hintsUsed + 1);
    const hint = challenge.hints[this.hintsUsed - 1] ?? challenge.hints[1];
    const text = this.options.panel.querySelector<HTMLElement>("#hint-text");
    if (text) text.textContent = hint;
    this.options.speak(hint);
    const button = this.options.panel.querySelector<HTMLButtonElement>(
      "[data-action='hint']",
    );
    if (button) {
      button.textContent =
        this.hintsUsed === 1 ? "Show a bigger hint" : "Hint shown";
      button.disabled = this.hintsUsed === 2;
    }
  }

  private finish(): void {
    const challenge = this.challenge;
    const body =
      this.options.panel.querySelector<HTMLElement>("#challenge-body");
    if (!challenge || !body || body.querySelector(".finish-button")) return;
    this.feedback(challenge.successExplanation, true);
    body.innerHTML = `<div class="finale-episode-complete" aria-hidden="true">✦</div><p class="challenge-question">This rescue is ready to join the sanctuary signal.</p>`;
    const button = document.createElement("button");
    button.type = "button";
    button.className = "primary-button finish-button";
    button.textContent = "Complete this rescue";
    button.addEventListener("click", () => {
      this.options.onComplete(challenge, this.hintsUsed);
      this.close();
    });
    body.append(button);
    button.focus();
  }
}

function renderVisual(step: FinaleStep): string {
  if (step.visual.kind === "bar-chart" && step.visual.bars) {
    const max = Math.max(...step.visual.bars.map(({ value }) => value));
    return `<figure class="finale-visual"><div class="review-chart" role="img" aria-label="${step.visual.caption}">${step.visual.bars.map(({ label, value }) => `<div><span style="height:${Math.round((value / max) * 100)}%"></span><strong>${label}</strong><em>${value}</em></div>`).join("")}</div><figcaption>${step.visual.caption}</figcaption></figure>`;
  }
  const icons: Record<FinaleStep["visual"]["kind"], string> = {
    skeleton: "🐇 🦴",
    muscle: "🦦 ⚙",
    animals: "🐢 🐾",
    germs: "🌻 🦔 🧑‍🌾",
    worm: "🪱 🌱",
    beacon: "✦ 🦎 ✦",
    "bar-chart": "▥",
  };
  return `<figure class="finale-visual finale-visual--scene"><div aria-hidden="true">${icons[step.visual.kind]}</div><figcaption>${step.visual.caption}</figcaption></figure>`;
}
